/**
 * Figure outline style (setting `figureOutline`, figureOutline.setting.ts): 0 = the dark ink outline (today's look),
 * 1 = the scene's outline: each outline pixel takes the line tone of the palette colour it borders (a darker step of
 * its own ramp, one step lighter on the lit upper-right side), as the selective outline of the scenery does
 * (truepx/clean.ts, same palette).
 *
 * Like the density and the true-pixel figure flag it is fixed for the page's life: figure frames live in worker
 * replies, the frame store, the bake and the atlas pages, so a change applies after a reload (the frame store's stamp
 * carries the mode, so each mode keeps its own frames). Workers get it in their first message.
 * Made once where the art is made (rig/finish.ts, the beasts), never per frame drawn.
 */
import { ctx2d, outline } from './pixel';
import { noOutlineOn, sceneOutlineOn } from './figureOutlineFlag';
import { nearestTone, palette } from './truepx/palette';

/**
 * The scene-style outline, `rings` canvas pixels deep. The first ring takes, from the commonest palette tone among the
 * pixel's four neighbours, that tone's line colour two steps down its ramp (one step down on the lit side: the outline
 * lies right of or above its shape); later rings copy the ring before.
 */
export function sceneOutline(c: HTMLCanvasElement, rings = 1): HTMLCanvasElement {
  const g = ctx2d(c);
  const { width: w, height: h } = c;
  const img = g.getImageData(0, 0, w, h);
  const px = new Uint32Array(img.data.buffer);
  const P = palette();
  const line = P.line;
  const down = P.down;
  let prev: Uint8Array | null = null;
  const nb = [0, 0, 0, 0];
  const tn = [0, 0, 0, 0];
  const lit = [true, true, false, false];
  for (let k = 0; k < rings; k++) {
    const src = px.slice();
    const mark = new Uint8Array(w * h);
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (src[i]! >>> 24) continue;
        // Neighbours in priority order: left, below (lit side), right, above.
        nb[0] = x > 0 ? i - 1 : -1;
        nb[1] = y < h - 1 ? i + w : -1;
        nb[2] = x < w - 1 ? i + 1 : -1;
        nb[3] = y > 0 ? i - w : -1;
        if (k === 0) {
          let n = 0;
          for (let d = 0; d < 4; d++) {
            const j = nb[d]!;
            if (j >= 0 && src[j]! >>> 24) {
              tn[d] = nearestTone(src[j]!);
              n++;
            } else tn[d] = -1;
          }
          if (!n) continue;
          let best = -1;
          let bc = 0;
          for (let d = 0; d < 4; d++) {
            if (tn[d]! < 0) continue;
            let cnt = 0;
            for (let e = 0; e < 4; e++) if (tn[e] === tn[d]) cnt++;
            if (cnt > bc) {
              bc = cnt;
              best = d;
            }
          }
          const m = tn[best]!;
          let sun = false;
          for (let d = 0; d < 4; d++) if (tn[d] === m && lit[d]) sun = true;
          const d1 = down[m]! >= 0 ? down[m]! : m;
          const t = sun ? d1 : down[d1]! >= 0 ? down[d1]! : d1;
          px[i] = ((line[t]! & 0xffffff) | 0xff000000) >>> 0;
          mark[i] = 1;
        } else {
          for (let d = 0; d < 4; d++) {
            const j = nb[d]!;
            if (j >= 0 && prev![j]) {
              px[i] = src[j]!;
              mark[i] = 1;
              break;
            }
          }
        }
      }
    prev = mark;
  }
  g.putImageData(img, 0, 0);
  return c;
}

/**
 * The outline of a figure or beast picture, `rings` canvas pixels deep: the flat ink `color` (the default is
 * `outline`'s, pixel for pixel as before) or, with the scene style on, the colour-following one.
 */
export function figOutline(c: HTMLCanvasElement, color?: string, rings = 1): HTMLCanvasElement {
  if (noOutlineOn()) return c;
  if (sceneOutlineOn()) return sceneOutline(c, rings);
  for (let k = 0; k < rings; k++) outline(c, color);
  return c;
}
